import './Modal.css';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../Common/LoadingIndicator';
import { createCategory, updateCategory, deleteCategory } from '../../api/categories';
import { getCurrentUser } from '../../api/users_institutions';
import { createDashboardHistoryLog } from '../../api/history_logs';

function ModalCategory({ Action, selectedCategory, closeModal}) {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleCreateCategory = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const name = document.getElementById('newCategoryName').value;
        const desc = document.getElementById('newCategoryDesc').value;
        const audience = document.querySelector('input[name="addCatAudience"]:checked')?.value || 'teachers';
        const is_visible = document.getElementById('newCategoryVisibility').checked;
        const categoryData = {
            name: name,
            description: desc,
            is_visible: is_visible,
            audience: audience,
        };

        try {
            const newCategory = await createCategory(categoryData);
            console.log('Category created:', newCategory);

            const logData = {
                user_id: getCurrentUser().id,
                log_title: `Created category: ${newCategory.name}`,
                log_date: new Date().toISOString(),
                log_data: {
                    category_id: newCategory.id,
                    category_name: newCategory.name,}
            };
            await createDashboardHistoryLog(logData);
            closeModal();
        } catch (error) {
            console.error('Error creating category:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateCategory = async (categoryId) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const name = document.getElementById('editCategoryName').value;
        const desc = document.getElementById('editCategoryDesc').value;
        const audience = document.querySelector('input[name="editCatAudience"]:checked')?.value || 'teachers';
        const is_visible = document.getElementById('editCategoryVisibility').checked;
        const categoryData = {
            name: name,
            description: desc,
            audience: audience,
            is_visible: is_visible,
        };

        try {
            const updatedCategory = await updateCategory(categoryId, categoryData);
            console.log('Category updated:', updatedCategory);

            const logData = {
                user_id: getCurrentUser().id,
                log_title: `Updated category: ${updatedCategory.name}`,
                log_date: new Date().toISOString(),
                log_data: {
                    category_id: updatedCategory.id,
                    category_name: updatedCategory.name,
                }
            };
            await createDashboardHistoryLog(logData);
            closeModal();
        } catch (error) {
            console.error('Error updating category:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            await deleteCategory(categoryId);
            console.log('Category deleted:', categoryId);

            const logData = {
                user_id: getCurrentUser().id,
                log_title: `Deleted category with ID: ${categoryId}`,
                log_date: new Date().toISOString(),
                log_data: {
                    category_id: categoryId,
                }
            };
            await createDashboardHistoryLog(logData);
            closeModal();
        } catch (error) {
            console.error('Error deleting category:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <LoadingIndicator active={isSubmitting} label={t('common.loading')} />
            <div className="Modal">
            {Action === 'Add' ? (
                <div className="modal-box">
                    <h2>{t('modalCategory.addNewCategory')}</h2>
                    <label className="label-text">Category Audience</label>
                    <div style={{ display: 'flex', gap: '15px', margin: '14px 0' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem', cursor: 'pointer' }}>
                            <input type="radio" name="addCatAudience" value="teachers" defaultChecked /> Teachers
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem', cursor: 'pointer' }}>
                            <input type="radio" name="addCatAudience" value="admins" /> Admins
                        </label>
                    </div>
                    <input
                        type="text"
                        id="newCategoryName"
                        placeholder={t('modalCategory.categoryNamePlaceholder')}
                    />
                    <textarea
                        id="newCategoryDesc"
                        placeholder={t('modalCategory.categoryDescPlaceholder')}
                    ></textarea>
                    <div className="toggleVisible" style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '15px 0' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{t('common.setVisible')}</div>
                        <label className="switch">
                            <input type="checkbox" id="newCategoryVisibility" defaultChecked={selectedCategory?.is_visible}/>
                            <span className="slider" ></span>
                        </label>
                    </div>
                    <div className="modal-buttons">
                        <button className="btn-primary" onClick={handleCreateCategory} disabled={isSubmitting}>
                            {t('common.create')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : Action === 'Update' ? (
                <div className="modal-box">
                    <h2>{t('modalCategory.editCategory')}</h2>
                    <label className="label-text">Category Audience</label>
                    <div style={{ display: 'flex', gap: '15px', margin: '14px 0' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem', cursor: 'pointer' }}>
                            <input type="radio" name="editCatAudience" value="teachers" defaultChecked={(selectedCategory?.audience || 'teachers') === 'teachers'} /> Teachers
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem', cursor: 'pointer' }}>
                            <input type="radio" name="editCatAudience" value="admins" defaultChecked={selectedCategory?.audience === 'admins'} /> Admins
                        </label>
                    </div>
                    <input
                        type="text"
                        id="editCategoryName"
                        placeholder={t('modalCategory.categoryNameFieldPlaceholder')}
                        defaultValue={selectedCategory?.name || ''}
                    />
                    <textarea
                        id="editCategoryDesc"
                        placeholder={t('modalCategory.categoryDescFieldPlaceholder')}
                        defaultValue={selectedCategory?.description || ''}
                    ></textarea>
                    <div className="toggleVisible" style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '15px 0' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{t('common.setVisible')}</div>
                        <label className="switch">
                            <input type="checkbox" id="editCategoryVisibility" defaultChecked={selectedCategory?.is_visible}/>
                            <span className="slider" ></span>
                        </label>
                    </div>
                    <div className="modal-buttons">
                        <button className="btn-primary" onClick={() => handleUpdateCategory(selectedCategory.id)} disabled={isSubmitting}>
                            {t('common.change')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : Action === 'Delete' ? (
                <div className="modal-box">
                    <h2>{t('common.confirmDeletion')}</h2>
                    <p>{t('modalCategory.deleteCategoryConfirm')}</p>
                    <div className="modal-buttons">
                        <button
                            className="btn-primary btn-delete"
                            onClick={() => handleDeleteCategory(selectedCategory.id)}
                            disabled={isSubmitting}
                        >
                            {t('common.delete')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
        </>
    );
}

export default ModalCategory;